import { inject, Injectable } from '@angular/core';
import { HttpService } from 'services/http/http.service';
import {
  Filters,
  TrackedCertificatesResponse,
} from 'features/user-profile/employer/certificates/employer-user-certificates.interfaces';
import {
  CertificatePhasesResponse,
  CertificateDetails,
  CertificatePhaseStepsData,
} from 'features/user-profile/shared/certificate-details/user-profile-certificate-details.interfaces';
import { Observable } from 'rxjs';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { buildQueryParams, convertFilterStructToQueryParam, createQueryParam } from 'utils/helpers';
import { UserCertificatesService } from 'features/user-profile/shared/certificate-details/user-profile-certificate-details.service';
import { EmployerAddUsersService } from 'features/employer-add-users/employer-add-users.service';
import { AvailableCertificateResponse } from 'features/employer-add-users/+state/employer-add-users.interfaces';
import { ShowIfHttpError } from '@certemy/common';

@Injectable()
export class EmployerUserCertificatesService implements UserCertificatesService {
  constructor(private http: HttpService, private employerAddUsersService: EmployerAddUsersService) {}

  @ShowIfHttpError('Cannot receive tracked credentials.')
  getTrackedCertificates(userId: number, filters: Filters = new Filters()): Observable<TrackedCertificatesResponse> {
    const filterQueryParam = convertFilterStructToQueryParam(filters);
    return this.http.get(`/employer/user/${userId}/certification/tracked?${filterQueryParam}`);
  }

  getAssignedCertificates(userId: number, filters: Filters = new Filters()): Observable<AvailableCertificateResponse> {
    return this.employerAddUsersService.getAvailableCertificatesForRequest(userId, filters);
  }

  // Don't remove: Used through DI as UserCertificatesService
  @ShowIfHttpError('Cannot receive credential details.')
  getCertificateDetails(certificateId: number): Observable<CertificateDetails> {
    return this.http.get(`/employer/user/certification/${certificateId}`);
  }

  // Don't remove: Used through DI as UserCertificatesService
  @ShowIfHttpError('Cannot receive credential phases.')
  getCertificatePhases(
    certificateId: number,
    params = { order_id: 'name', order_type: 'asc', page: 1, page_size: 10 },
  ): Observable<CertificatePhasesResponse> {
    const queryParams = buildQueryParams([
      createQueryParam('page', params.page),
      createQueryParam('page_size', params.page_size),
      createQueryParam('order_id', params.order_id),
      createQueryParam('order_type', params.order_type),
    ]);
    return this.http.get(`/employer/user/certification/${certificateId}/phases?${queryParams}`);
  }

  // Don't remove: Used through DI as UserCertificatesService
  @ShowIfHttpError('Cannot receive steps.')
  getCertificatePhaseSteps(certificateId: number, phaseId: number): Observable<CertificatePhaseStepsData> {
    return this.http.get(`/employer/user/certification/${certificateId}/phases/${phaseId}/steps`);
  }

  @ShowIfHttpError('An error occurred while removing request.')
  removeTrackRequest(requestId) {
    return this.http.delete(`/employer/certification/request/${requestId}`);
  }
}

export const employerUserTrackedCertificatesResolver: ResolveFn<Observable<TrackedCertificatesResponse>> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
  const { userId } = route.params;
  return inject(EmployerUserCertificatesService).getTrackedCertificates(userId);
};

export const employerUserAssignedCertificatesResolver: ResolveFn<Observable<AvailableCertificateResponse>> = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
  const { userId } = route.params;
  return inject(EmployerUserCertificatesService).getAssignedCertificates(userId);
};
